import type { FC, ReactNode } from 'react';

import { Placeholder, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { CmsHeroShell } from '@eneco/components/CmsHeroShell/CmsHeroShell';
import { ButtonLink, Heading, Stack, Text } from '@eneco/sparky';
import type { BoxProps } from '@eneco/sparky/types';

import type { HeroContent } from './hero-content.types';
import { HERO_SLOTS, resolveSlot } from './slots';

/**
 * `Hero` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Layout comes from `CmsHeroShell` — the same markup the legacy Sitecore lane renders
 * (`libs/sitecore/components/content/src/Hero/HeroInner.tsx`) — so a `?_cms=sitecore` / `?_cms=sanity`
 * comparison differs in content only. This component contributes the CMS wiring:
 *
 * - **Image**: `content.image` (Sitecore + CMS schema) or `content.backgroundImage` (POC),
 *   resolved through the provider's own image mapper.
 * - **Appearance**: `useSetting`, which reads Sitecore rendering `params` and the CMS schema's
 *   flattened `appearance` fields alike — previously these were read from `content` only, so the
 *   Sitecore lane rendered every Hero unstyled.
 * - **Slots**: a Hero has **no text fields on either provider**; its headline and CTAs are child
 *   renderings. Slot names differ per CMS, so they are resolved (`slots.ts`) rather than hard-coded.
 *
 * The `title`/`subtitle`/`buttons` block is POC-schema-only (lowercase `hero`), whose fields do carry
 * text; it renders in the left column when present so that dataset keeps working.
 */
const Hero: FC = () => {
  const rendering = useRendering();
  const content = useContent<HeroContent>();
  const mapImage = useImageMapper();

  const backgroundColor = useSetting('backgroundColor') as BoxProps['backgroundColor'];
  const backgroundColorBottom = useSetting('backgroundColorBottom') as BoxProps['backgroundColor'];
  const isVariableHeight = useSetting('height') === 'variable';
  const isVariableWidth = useSetting('width') === 'variable';
  const isMinMargin = useSetting('minMargin') === 'true';

  // `image` is Sitecore's and the CMS schema's field name; `backgroundImage` is the POC's.
  const imageProps = mapImage(content.image ?? content.backgroundImage);

  const promoSlot = resolveSlot(rendering, HERO_SLOTS.promo);
  const leftSlot = resolveSlot(rendering, HERO_SLOTS.left);
  const rightSlot = resolveSlot(rendering, HERO_SLOTS.right);
  const bottomSlot = resolveSlot(rendering, HERO_SLOTS.bottom);

  const title = pocValue(content.title);
  const subtitle = pocValue(content.subtitle);
  const hasPocText = Boolean(title || subtitle || content.buttons?.length);

  // Slot props must be `undefined` when empty, not an element that renders null: the shell keys its
  // padding and the bottom slot's bleed off slot *presence*.
  const left = joinNodes(
    leftSlot ? <Placeholder key="slot" name={leftSlot} /> : undefined,
    hasPocText ? <PocHeroText key="poc" content={content} title={title} subtitle={subtitle} /> : undefined,
  );

  return (
    <CmsHeroShell
      image={imageProps?.src ? { src: imageProps.src, alt: imageProps.alt, sources: imageProps.sources } : undefined}
      backgroundColor={backgroundColor}
      backgroundColorBottom={backgroundColorBottom}
      isVariableHeight={isVariableHeight}
      isVariableWidth={isVariableWidth}
      isMinMargin={isMinMargin}
      promo={promoSlot ? <Placeholder name={promoSlot} /> : undefined}
      left={left}
      right={rightSlot ? <Placeholder name={rightSlot} /> : undefined}
      bottom={bottomSlot ? <Placeholder name={bottomSlot} /> : undefined}
    />
  );
};

const joinNodes = (...nodes: readonly (ReactNode | undefined)[]): ReactNode | undefined => {
  const present = nodes.filter(Boolean);
  if (present.length === 0) return undefined;
  if (present.length === 1) return present[0];
  return <>{present}</>;
};

/** POC schema only: its `hero` text fields are audience-segmented objects; render the default variant. */
const pocValue = (value: HeroContent['title']): string | undefined => (typeof value === 'string' ? value : value?.default);

const PocHeroText: FC<{ content: HeroContent; title?: string; subtitle?: string }> = ({ content, title, subtitle }) => {
  return (
    <Stack gap="6">
      {title && (
        <Heading as="h1" size="2XL">
          {title}
        </Heading>
      )}
      {subtitle && <Text>{subtitle}</Text>}
      {content.buttons && content.buttons.length > 0 && (
        <Stack direction="row" gap="4">
          {content.buttons.map(button => {
            const link = linkWithUrl(button.link);
            if (!link) return null;
            return (
              <ButtonLink key={button._key} href={link.href} target={link.target} action={button.variant ?? 'primary'}>
                {button.label}
              </ButtonLink>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default Hero;
